// Client-side signal for "you must enrol a second factor before continuing".
//
// THE PROBLEM. plugins/auth.ts returns 403 `mfa_enrollment_required` when a
// user holds neither a verified GoTrue factor nor a passkey and their tenant
// requires MFA — and it sends a genuinely useful sentence with it. Nothing in
// the web app read that code. Not a component, not a hook, not the API layer.
//
// So the user landed on a live-looking app where every single call failed with
// a generic error and no route to the one screen that fixes it. That is the
// same shape as the unrecoverable-401 problem session-expiry.ts was written
// for — "the user sees a live-looking page where every call fails, and it
// reads as 'feature X broke' rather than 'you were signed out'" — and it gets
// the same treatment here.
//
// WHO HITS IT. A brand-new tenant owner, every time. tenants.mfa_required
// defaults to true, and an owner provisioned through the platform console has
// no factor and no passkey at the moment they first sign in. So the first
// customer onboarded through that flow met a dead dashboard.
//
// Dependency-free (no React) so lib/api.ts imports it without a cycle, exactly
// like its sibling.

/** The server's own explanation, passed through rather than reworded. */
type Listener = (detail: string | null) => void;

const listeners = new Set<Listener>();

/** Subscribe to the enrolment-required signal. Returns an unsubscribe fn. */
export function onMfaEnrollmentRequired(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Signal that a request was refused because the user has no second factor.
 *
 * Safe to call on every such response: listeners are expected to be
 * idempotent, because a dashboard fires several requests at once and all of
 * them will be refused for the same reason.
 */
export function notifyMfaEnrollmentRequired(detail: string | null): void {
  for (const fn of listeners) {
    try {
      fn(detail);
    } catch {
      /* one listener throwing must not stop the others */
    }
  }
}

/**
 * Does this failed response mean "enrol a factor"?
 *
 * Narrow on purpose. The auth plugin returns TWO different 403s: this one, and
 * `mfa_required`, which means "you have a factor, now step up with it". They
 * need opposite treatments — one sends you to enrol, the other to verify — and
 * conflating them would send somebody who already holds a passkey to a screen
 * telling them to get one.
 */
export function isMfaEnrollmentRequired(
  status: number,
  body: unknown,
): { required: true; detail: string | null } | { required: false } {
  if (status !== 403 || !body || typeof body !== "object") return { required: false };
  const b = body as { error?: unknown; detail?: unknown };
  if (b.error !== "mfa_enrollment_required") return { required: false };
  return {
    required: true,
    detail: typeof b.detail === "string" ? b.detail : null,
  };
}


// ---------------------------------------------------------------------------
// The sibling this file's own comment describes, and nobody built.
//
// The auth plugin returns TWO 403s. `mfa_enrollment_required` has had a
// listener since it was introduced. `mfa_required` — "you hold a factor, now
// step up with it" — had none, so a session that was refused for it left the
// user on a live-looking app where every call failed with a generic error.
// That is the exact state the enrolment redirect exists to prevent, one gate
// over.
//
// It was reachable in production by the most ordinary route there is: enrol
// TOTP from Settings → Security, whose elevated session never reached the API
// cookie, and every request afterwards was refused with no way forward.
// ---------------------------------------------------------------------------

type StepUpListener = () => void;
const stepUpListeners = new Set<StepUpListener>();

export function isMfaStepUpRequired(status: number, body: unknown): boolean {
  if (status !== 403 || !body || typeof body !== "object") return false;
  return (body as { error?: unknown }).error === "mfa_required";
}

export function onMfaStepUpRequired(fn: StepUpListener): () => void {
  stepUpListeners.add(fn);
  return () => stepUpListeners.delete(fn);
}

export function notifyMfaStepUpRequired(): void {
  for (const fn of [...stepUpListeners]) {
    try {
      fn();
    } catch {
      /* one bad listener must not stop the others */
    }
  }
}
